import {
  useHttpClient,
  useNetworkStatus,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { AppointmentProposalOptionModifier } from '../../../data-access/appointmentProposalOptionModifiers';
import { AppointmentProposalOptionProduct } from '../../../data-access/appointmentProposalOptionProducts';
import { AppointmentProposalOption } from '../../../data-access/appointmentProposalOptions';
import { AppointmentProposalOptionSOW } from '../../../data-access/appointmentProposalOptionSows';
import { AppointmentProposal } from '../../../data-access/appointmentProposals';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';

type PullDocument =
  | AppointmentProposal
  | AppointmentProposalOption
  | AppointmentProposalOptionProduct
  | AppointmentProposalOptionModifier
  | AppointmentProposalOptionSOW;

type PullResponse = {
  documents: PullDocument[];
  checkpoint: { updatedAt: string; id: string } | null;
};

const PULL_BATCH_SIZE = 500;

interface AppointmentProposalSyncContextProps {
  isSyncing: boolean;
  pushSyncError: string | null;
  syncAppointment: (appointmentId: string) => Promise<void>;
}

const AppointmentProposalSyncContext =
  createContext<AppointmentProposalSyncContextProps | null>(null);

export const AppointmentProposalSyncProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const data = useOfflineDbData();
  const httpClient = useHttpClient();
  const [isSyncing, setIsSyncing] = useState<string[]>([]);
  const [pushSyncError, setPushSyncError] = useState<string | null>(null);
  const promRef = useRef<Map<string, Promise<void>>>(new Map());
  const checkpointRef = useRef<Map<string, PullResponse['checkpoint']>>(
    new Map()
  );
  const isOnline = useNetworkStatus();

  const upsertSynced = useCallback(
    async <T extends PullDocument>(documents: T[]) => {
      const proposals: AppointmentProposal[] = [];
      const options: AppointmentProposalOption[] = [];
      const products: AppointmentProposalOptionProduct[] = [];
      const modifiers: AppointmentProposalOptionModifier[] = [];
      const sows: AppointmentProposalOptionSOW[] = [];

      for (const doc of documents) {
        switch (doc.entityType) {
          case 'APPOINTMENT_PROPOSAL':
            proposals.push(doc);
            break;
          case 'APPOINTMENT_PROPOSAL_OPTION':
            options.push(doc);
            break;
          case 'APPOINTMENT_PROPOSAL_OPTION_PRODUCT':
            products.push(doc);
            break;
          case 'APPOINTMENT_PROPOSAL_OPTION_MODIFIER':
            modifiers.push(doc);
            break;
          case 'APPOINTMENT_PROPOSAL_OPTION_SOW':
            sows.push(doc);
            break;
        }
      }

      await Promise.all([
        data.bulkUpsertSynced('appointment_proposals', proposals),
        data.bulkUpsertSynced('appointment_proposal_options', options),
        data.bulkUpsertSynced('appointment_proposal_option_products', products),
        data.bulkUpsertSynced(
          'appointment_proposal_option_modifiers',
          modifiers
        ),
        data.bulkUpsertSynced('appointment_proposal_option_sows', sows),
      ]);
    },
    [data]
  );

  const fetchLatestData: AppointmentProposalSyncContextProps['syncAppointment'] =
    useCallback(
      async (appointmentId) => {
        if (!isOnline()) {
          return;
        }

        setIsSyncing((prev) => [...prev, appointmentId]);

        try {
          let checkpoint = checkpointRef.current.get(appointmentId) ?? null;
          let hasMore = true;

          while (hasMore) {
            const params = new URLSearchParams({
              appointmentIds: appointmentId,
              batchSize: `${PULL_BATCH_SIZE}`,
            });

            if (checkpoint) {
              params.set('since', checkpoint.updatedAt);
              params.set('sinceId', checkpoint.id);
            }

            const response = await httpClient.get<PullResponse>(
              `/appointments/sync/pull?${params}`
            );

            if (!response.documents.length) {
              hasMore = false;
              break;
            }

            hasMore = response.documents.length === PULL_BATCH_SIZE;
            checkpoint = response.checkpoint;
            checkpointRef.current.set(appointmentId, response.checkpoint);

            await upsertSynced(response.documents);
          }
        } catch {
          checkpointRef.current.delete(appointmentId);
        } finally {
          setIsSyncing((prev) => prev.filter((id) => id !== appointmentId));
        }
      },
      [httpClient, isOnline, upsertSynced]
    );

  const syncAppointment: AppointmentProposalSyncContextProps['syncAppointment'] =
    useCallback(
      async (appointmentId) => {
        if (!promRef.current.has(appointmentId)) {
          setPushSyncError(null);
          const fetchPromise = fetchLatestData(appointmentId);
          promRef.current.set(appointmentId, fetchPromise);
          fetchPromise
            .catch((error) => setPushSyncError(`${error}`))
            .finally(() => {
              setTimeout(() => promRef.current.delete(appointmentId), 250);
            });
          return fetchPromise;
        }
        return promRef.current.get(appointmentId);
      },
      [fetchLatestData]
    );

  return (
    <AppointmentProposalSyncContext.Provider
      value={{
        isSyncing: !!isSyncing.length,
        pushSyncError,
        syncAppointment,
      }}
    >
      {children}
    </AppointmentProposalSyncContext.Provider>
  );
};

export const useAppointmentProposalSync = () => {
  const context = useContext(AppointmentProposalSyncContext);
  if (!context) {
    throw new Error(
      'useAppointmentProposalSync must be used within an AppointmentProposalSyncProvider'
    );
  }
  return context;
};
